// SpravaAppiekTab.jsx — SPRAVA_APPIEK_V1 (23. 10. 2026, session KSBX APPKY): Nastavenia › Správa appiek.
// Presne podľa mockupu MOCKUP_Sprava_appiek_2026-10-23.html (Mirko „mockup super“, Kronika ksbx-appky 3047; zadanie 3030, 3033).
// Kto čo vidí v mobilných appkách: karty appiek, moduly appky Matrix s predvolenými rolami a pri module ľudia
// s voľbou Podľa roly / Áno / Nie. Ukladá sa do users.nav_permissions (extra_modules / hidden_modules) — ten istý register
// ako Používatelia › Individuálne práva (0.J); server GET/PUT /api/mobile/sprava, meniť smie len admin.
import React, { useState, useEffect, useMemo } from 'react'
import { useDirtyFlag } from '../hooks/useDirtyState.js'
import { COLORS, TYPO, SPACE, RADIUS, hladaj } from '../theme.js'
import { Btn, BtnGhost } from './Btn.jsx'
import { fetchAuth } from '../auth/fetchAuth.js'
import { useToast } from './Toast.jsx'

const karta = { background: COLORS.bg, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.lg, padding: 14 }
const nadpis = { fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.extra, letterSpacing: '.06em', textTransform: 'uppercase', color: COLORS.muted, margin: '0 0 10px' }
const poznamka = { color: COLORS.muted, fontSize: TYPO.sizes.sm, marginTop: SPACE.sm, lineHeight: 1.45 }
const stitok = (druh) => ({
  display: 'inline-block', fontSize: TYPO.sizes.xs, fontWeight: TYPO.weights.bold, padding: '2px 8px', borderRadius: RADIUS.full,
  border: `1px solid ${druh === 'zlta' ? '#FDE68A' : COLORS.line}`, color: druh === 'zlta' ? COLORS.warn : COLORS.muted,
  background: druh === 'zlta' ? '#FFFBEB' : COLORS.bg,
})
const th = { textAlign: 'left', fontSize: TYPO.sizes.xs, letterSpacing: '.06em', textTransform: 'uppercase', color: COLORS.muted, fontWeight: TYPO.weights.extra, padding: 8, borderBottom: `1px solid ${COLORS.line}` }
const td = { padding: 8, borderBottom: `1px solid ${COLORS.line}`, verticalAlign: 'middle' }

// stav človeka pri module: 'ano' (pridaný), 'nie' (odobratý), 'rola' (podľa roly)
const stavCloveka = (c, modul) => c.extra.includes(modul) ? 'ano' : (c.hidden.includes(modul) ? 'nie' : 'rola')
const vidi = (c, m, stav) => stav === 'ano' || (stav === 'rola' && m.roly.includes(c.rola))

export default function SpravaAppiekTab() {
  const toast = useToast()
  const [d, setD] = useState(null)
  const [chyba, setChyba] = useState('')
  const [appka, setAppka] = useState('matrix')
  const [modul, setModul] = useState('blocky')
  const [q, setQ] = useState('')
  const [draft, setDraft] = useState({})          // { 'userId|modul': 'rola'|'ano'|'nie' } — len zmeny oproti serveru
  const [busy, setBusy] = useState(false)

  const nacitaj = () => fetchAuth('/api/mobile/sprava').then(async r => {
    const j = await r.json().catch(() => ({}))
    if (!r.ok) { setChyba(j.error || ('Chyba ' + r.status)); return }
    setD(j); setChyba(''); setDraft({})
    if (!j.moduly.some(m => m.id === modul) && j.moduly[0]) setModul(j.moduly[0].id)
  }).catch(e => setChyba(e.message))
  useEffect(() => { nacitaj() }, [])   // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = Object.keys(draft).length > 0
  const ulozit = async () => {
    if (!dirty || busy) return
    setBusy(true)
    try {
      const zmeny = Object.entries(draft).map(([k, stav]) => { const [user_id, m] = k.split('|'); return { user_id: Number(user_id), modul: m, stav } })
      const r = await fetchAuth('/api/mobile/sprava', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ zmeny }) })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) { toast.error(j.error || 'Uloženie zlyhalo'); return }
      toast.success('Uložené — človeku sa zmena prejaví pri ďalšom otvorení appky')
      await nacitaj()
    } catch (e) { toast.error('Chyba: ' + e.message) } finally { setBusy(false) }
  }
  useDirtyFlag(dirty, { key: 'nastavenia-sprava-appiek', save: ulozit, discard: () => setDraft({}) })

  const stav = (c, m) => draft[c.id + '|' + m] || stavCloveka(c, m)
  const nastav = (c, m, s) => setDraft(prev => {
    const n = { ...prev }, k = c.id + '|' + m
    if (s === stavCloveka(c, m)) delete n[k]; else n[k] = s
    return n
  })

  const mod = d ? d.moduly.find(m => m.id === modul) : null
  const pocet = (m) => d ? d.ludia.filter(c => vidi(c, m, stav(c, m.id))).length : 0
  const ludia = useMemo(() => {
    if (!d || !mod) return []
    const l = q.trim() ? d.ludia.filter(c => hladaj(`${c.meno} ${c.login} ${c.rola_text}`, q.trim())) : d.ludia
    // kto modul v appke vidí, je hore (ako v mockupe), potom podľa mena — poradie podľa ULOŽENÉHO stavu,
    // aby riadok pri ťuknutí na Áno / Nie neodskočil spod myši
    return [...l].sort((a, b) => (vidi(b, mod, stavCloveka(b, mod.id)) - vidi(a, mod, stavCloveka(a, mod.id))) || a.meno.localeCompare(b.meno, 'sk'))
  }, [d, mod, q])

  if (chyba) return <div style={{ ...karta, color: COLORS.remove }}>{chyba}</div>
  if (!d) return <div style={{ ...karta, color: COLORS.muted }}>Načítavam…</div>
  const app = d.appky.find(a => a.id === appka) || d.appky[0]

  return (
    // paddingBottom: Uložiť je vpravo dole ako v mockupe — miesto, aby ho pri zrolovaní nezakryla plávajúca ikona Trinity
    <div style={{ display: 'flex', flexDirection: 'column', gap: SPACE.md, paddingBottom: 72 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: SPACE.md, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: TYPO.weights.extra }}>Správa appiek</h2>
        <span style={{ color: COLORS.muted, fontSize: TYPO.sizes.base }}>Kto čo vidí v mobilných appkách. Predvolené dáva rola, navyše alebo menej zaškrtneš tu.</span>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: SPACE.md }}>
        {d.appky.map(a => {
          const on = a.id === appka
          return (
            <div key={a.id} role="button" tabIndex={0} onClick={() => setAppka(a.id)} onKeyDown={e => { if (e.key === 'Enter') setAppka(a.id) }}
              style={{ ...karta, cursor: 'pointer', borderColor: on ? COLORS.nav : COLORS.line, background: on ? COLORS.navBg : COLORS.bg }}>
              <h3 style={{ margin: '0 0 4px', fontSize: 16 }}>{a.nazov}</h3>
              <div style={{ color: COLORS.muted, fontSize: TYPO.sizes.sm, fontFamily: TYPO.mono }}>{a.bundle} · {a.verzia}</div>
              <div style={{ marginTop: SPACE.sm, fontSize: TYPO.sizes.sm }}><span style={stitok()}>{a.obchod}</span>&nbsp; {a.popis}</div>
            </div>
          )
        })}
      </div>

      {!app.moduly ? (
        <div style={{ ...karta, color: COLORS.muted }}>
          Záložky appky {app.nazov} určuje rola človeka (zoznam zo servera). Nastavenie po ľuďoch má zatiaľ len appka Matrix.
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: '300px minmax(0, 1fr)', gap: SPACE.md, alignItems: 'start' }}>
          <div style={karta}>
            <p style={nadpis}>Moduly appky {app.nazov}</p>
            {d.moduly.map(m => {
              const on = m.id === modul
              return (
                <div key={m.id} role="button" tabIndex={0} onClick={() => setModul(m.id)} onKeyDown={e => { if (e.key === 'Enter') setModul(m.id) }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 10, border: `1px solid ${on ? COLORS.nav : COLORS.line}`, borderRadius: RADIUS.md,
                    marginBottom: SPACE.sm, cursor: 'pointer', background: on ? COLORS.navBg : COLORS.bg }}>
                  <div style={{ flex: 1 }}>
                    <b style={{ display: 'block' }}>{m.label}{m.novy ? <> <span style={stitok('zlta')}>nový</span></> : null}</b>
                    <span style={{ color: COLORS.muted, fontSize: TYPO.sizes.sm }}>{m.popis ? m.popis + ' · ' : ''}{m.roly_text}</span>
                  </div>
                  <span style={{ fontFamily: TYPO.mono, fontWeight: TYPO.weights.semibold }} title="Koľko ľudí modul v appke vidí">{pocet(m)}</span>
                </div>
              )
            })}
            <p style={poznamka}>Pri module sa ukáže, koľko ľudí ho v appke vidí (rola + zaškrtnutí − odobratí).</p>
          </div>

          {mod && (
            <div style={karta}>
              <p style={nadpis}>{mod.label} — kto vidí v appke {app.nazov}</p>
              {mod.info ? (
                <div style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: RADIUS.md, padding: 10, fontSize: TYPO.sizes.base, marginBottom: SPACE.md }}>{mod.info}</div>
              ) : null}
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Hľadať človeka…"
                style={{ width: '100%', padding: '8px 10px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, fontFamily: 'inherit', fontSize: TYPO.sizes.md, marginBottom: SPACE.sm, boxSizing: 'border-box' }} />
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Človek</th><th style={th}>Rola</th><th style={th}>Podľa roly</th><th style={th}>V appke</th></tr></thead>
                <tbody>
                  {ludia.map(c => {
                    const s = stav(c, mod.id), ma = vidi(c, mod, s)
                    return (
                      <tr key={c.id} style={{ background: ma ? '#FAFCFF' : undefined }}>
                        <td style={td}>{ma ? <b>{c.meno}</b> : c.meno}</td>
                        <td style={{ ...td, color: COLORS.muted, fontSize: TYPO.sizes.sm }}>{c.rola_text}</td>
                        <td style={td}>{mod.roly.includes(c.rola) ? 'áno' : 'nie'}</td>
                        <td style={td}>
                          <div style={{ display: 'inline-flex', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, overflow: 'hidden' }}>
                            {[['rola', 'Podľa roly'], ['ano', 'Áno'], ['nie', 'Nie']].map(([k, t]) => (
                              <button key={k} type="button" disabled={!d.smiem_menit || busy} onClick={() => nastav(c, mod.id, k)}
                                style={{ border: 0, padding: '6px 10px', fontFamily: 'inherit', fontWeight: TYPO.weights.semibold, fontSize: TYPO.sizes.sm,
                                  cursor: d.smiem_menit ? 'pointer' : 'default', background: s === k ? COLORS.nav : COLORS.bg, color: s === k ? '#fff' : COLORS.muted }}>{t}</button>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
              <p style={poznamka}>Áno / Nie sa ukladá k človeku (to isté, čo Používatelia → Individuálne práva). Appka si zoznam berie pri štarte —
                zmena sa človeku prejaví po ďalšom otvorení appky, nič sa nemusí preinštalovať.</p>
              {d.smiem_menit ? (
                <div style={{ display: 'flex', gap: SPACE.sm, justifyContent: 'flex-end', marginTop: SPACE.md }}>
                  <BtnGhost onClick={() => setDraft({})} disabled={!dirty || busy}>Zrušiť</BtnGhost>
                  <Btn variant="nav" onClick={ulozit} disabled={!dirty} loading={busy}>Uložiť</Btn>
                </div>
              ) : (
                <p style={poznamka}>Meniť, kto čo vidí, smie len admin.</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
